// ─── ObrasView — gestão de Obras (estaleiros) · variante mercadoria (Casais) ──
//
// Migração da entidade "Obra" da app CasaisHub. Lista + CRUD. Campos espelham o
// diálogo "Editar Obra": Código de Obra*, PEP/CC (SAP), Nome/Descrição, Empresa,
// Coordenadas, Responsável (nome/telefone/email) + contactos extra, tipo, ativa,
// datas. Montado quando activeView === 'obras' (card no hub Manutenção, gated
// uiVariant==='mercadoria').

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useFormCardScroll } from '../../utils/manageHelpers';
import {
  Box, Typography, Button, TextField, IconButton, Tooltip, Chip, Switch,
  FormControlLabel, CircularProgress,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles, ThemeProvider } from '@material-ui/core/styles';
import { usePlannerTheme, usePlannerDialogTheme, usePlannerToolbarTheme } from '../../theme/plannerTheme';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import PlaceIcon from '@material-ui/icons/Place';
import EngineeringIcon from '@material-ui/icons/Build';
import PhoneIcon from '@material-ui/icons/Phone';
import { setActiveView } from '../../store/slices/uiSlice';
import { fetchLocations } from '../../store/slices/locationsSlice';
import { useDispatch } from 'react-redux';
import { AuthContext } from '../../App';
import ConfirmDialog from '../common/ConfirmDialog';
import { geocodeSearchMulti } from '../../utils/mapboxApi';

// Identidade ecooGPS — só aplicada em Claro/Escuro (Clássico mantém o azul atual).
const BRAND = 'linear-gradient(100deg, #58BAE6 0%, #18BDEE 45%, #2FD596 100%)';
const BRAND_DARK = 'linear-gradient(100deg, #6EC1E4 0%, #20C6F4 45%, #3DDC97 100%)';
const ON_BRAND = '#03121c';

const useStyles = makeStyles(() => ({
  root: { width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surf-page, #f8fafc)', overflow: 'hidden' },
  header: { display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px', background: 'var(--surf-card, #fff)', borderBottom: '1px solid var(--surf-border, #e2e8f0)' },
  titleBox: { flex: 1, minWidth: 0 },
  title: { fontSize: '1.05rem', fontWeight: 800, color: 'var(--surf-title, #0f172a)', display: 'flex', alignItems: 'center', gap: 8 },
  headerIcon: { width: 34, height: 34, borderRadius: 10, background: '#e0f2fe', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  subtitle: { fontSize: '0.7rem', color: 'var(--surf-muted, #64748b)', marginTop: 2 },
  toolbar: { display: 'flex', gap: 8, padding: '12px 24px', alignItems: 'center', borderBottom: '1px solid var(--surf-border-soft, #f1f5f9)', background: 'var(--surf-card, #fff)', flexWrap: 'wrap' },
  searchInput: { flex: 1, maxWidth: 360, '& .MuiOutlinedInput-root': { borderRadius: 8, background: 'var(--surf-input-bg, #fff)', fontSize: '0.78rem' } },
  addButton: { background: '#0369a1', color: '#fff', borderRadius: 8, textTransform: 'none', fontWeight: 700, padding: '6px 16px', fontSize: '0.74rem', boxShadow: 'none', '&:hover': { background: '#075985' } },
  saveButton: { background: '#0369a1', color: '#fff', textTransform: 'none', fontSize: '0.78rem', fontWeight: 700, padding: '6px 18px', boxShadow: 'none', '&:hover': { background: '#075985' } },
  list: { flex: 1, overflowY: 'auto', padding: '16px 24px' },
  card: { background: 'var(--surf-card, #fff)', borderRadius: 12, padding: '14px 18px', marginBottom: 10, border: '1px solid var(--surf-border, #e2e8f0)', display: 'flex', alignItems: 'center', gap: 14, transition: 'all 0.2s', '&:hover': { boxShadow: 'var(--surf-shadow, 0 4px 12px rgba(0,0,0,0.06))', borderColor: 'var(--surf-accent-border, #cbd5e1)' } },
  cardInactive: { opacity: 0.55, background: 'var(--surf-card-alt, #f8fafc)' },
  cardIcon: { width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e0f2fe' },
  cardMain: { flex: 1, minWidth: 0 },
  cardName: { fontSize: '0.92rem', fontWeight: 700, color: 'var(--surf-strong, #0f172a)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardMeta: { fontSize: '0.7rem', color: 'var(--surf-muted, #64748b)', marginTop: 4, display: 'flex', gap: 12, flexWrap: 'wrap' },
  cardActions: { display: 'flex', gap: 4, alignItems: 'center' },
  empty: { padding: '80px 24px', textAlign: 'center', color: 'var(--surf-muted2, #94a3b8)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 },
  formCard: { background: 'var(--surf-card, #fff)', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid var(--surf-border, #e2e8f0)', boxShadow: 'var(--surf-shadow, 0 4px 16px rgba(0,0,0,0.05))', display: 'flex', flexDirection: 'column', gap: 14 },
  formTitle: { fontSize: '0.86rem', fontWeight: 700, color: 'var(--surf-strong, #0f172a)', display: 'flex', alignItems: 'center', gap: 8 },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  sectionLbl: { fontSize: '0.68rem', fontWeight: 700, color: 'var(--surf-muted, #64748b)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 },
  formActions: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 },
}));

const EMPTY_FORM = {
  codigoObra: '', pepCc: '', name: '', empresa: '', morada: '', coordsText: '',
  tipo: 'obra', isActive: true, dataInicio: '', dataFecho: '',
  responsavelNome: '', responsavelTelefone: '', responsavelEmail: '',
  contactosExtra: [], notes: '',
  motoristaHabitual: null, viaturaHabitual: null,
  // Articulação com favorito: estado desejado (toggle) + estado original +
  // modo quando se liga de novo ('create' novo favorito | 'link' existente).
  favorito: false, hadFavorite: false, favMode: 'create', favLinkId: null,
};

function parseCoords(text) {
  if (!text || !text.trim()) return { lat: null, lng: null };
  const parts = text.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);
  const lat = Number(parts[0]); const lng = Number(parts[1]);
  return { lat: Number.isFinite(lat) ? lat : null, lng: Number.isFinite(lng) ? lng : null };
}
function fmtCoords(lat, lng) {
  if (lat == null || lng == null) return '';
  return `${lat}, ${lng}`;
}
function toDateInput(v) {
  if (!v) return '';
  try { return new Date(v).toISOString().slice(0, 10); } catch { return ''; }
}

function ObrasView({ lang = 'pt' }) {
  const classes = useStyles();
  const theme = usePlannerTheme();
  const tbTheme = usePlannerToolbarTheme();
  const newUI = tbTheme !== 'classic'; // identidade ecooGPS só em Claro/Escuro
  const brandGrad = theme === 'dark' ? BRAND_DARK : BRAND;
  const dlgTheme = usePlannerDialogTheme();
  const dispatch = useDispatch();
  const { token, clientSettings } = useContext(AuthContext);
  const pt = lang === 'pt';

  const [obras, setObras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const formCardRef = useFormCardScroll(editingId);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  // Listas do EcooGPS para os "habituais" (dropdowns).
  const [driversList, setDriversList] = useState([]);
  const [vehiclesList, setVehiclesList] = useState([]);
  // Favoritos do cliente — para "associar a um favorito existente".
  const [favList, setFavList] = useState([]);
  // Geocodificação da morada (morada → coordenadas), igual aos favoritos.
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  const [addrFocused, setAddrFocused] = useState(false);
  const addrDebounceRef = React.useRef(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    if (!token) return;
    const h = { Authorization: `Bearer ${token}` };
    fetch('/planner-api/drivers', { headers: h }).then(r => r.ok ? r.json() : []).then(d => setDriversList(Array.isArray(d) ? d : [])).catch(() => {});
    fetch('/planner-api/vehicles?includeDisabled=true', { headers: h }).then(r => r.ok ? r.json() : []).then(v => setVehiclesList(Array.isArray(v) ? v : [])).catch(() => {});
    fetch('/planner-api/locations', { headers: h }).then(r => r.ok ? r.json() : []).then(l => setFavList(Array.isArray(l) ? l : [])).catch(() => {});
  }, [token]);

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError(null);
    try {
      const qs = showInactive ? '?includeInactive=1' : '';
      const r = await fetch(`/planner-api/obras${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setObras(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Obras fetch error:', e);
      setError(pt ? 'Não foi possível carregar as obras.' : 'Could not load works.');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, showInactive, pt]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  function startCreate() { setForm(EMPTY_FORM); setEditingId('new'); }
  function startEdit(o) {
    setForm({
      codigoObra: o.codigoObra || '', pepCc: o.pepCc || '', name: o.name || '', empresa: o.empresa || '',
      morada: o.morada || '',
      coordsText: fmtCoords(o.lat, o.lng), tipo: o.tipo || 'obra', isActive: o.isActive !== false,
      dataInicio: toDateInput(o.dataInicio), dataFecho: toDateInput(o.dataFecho),
      responsavelNome: o.responsavelNome || '', responsavelTelefone: o.responsavelTelefone || '', responsavelEmail: o.responsavelEmail || '',
      contactosExtra: Array.isArray(o.contactosExtra) ? o.contactosExtra : [], notes: o.notes || '',
      motoristaHabitual: o.motoristaHabitualExternalId || o.motoristaHabitualNome
        ? { externalId: o.motoristaHabitualExternalId || null, name: o.motoristaHabitualNome || '' } : null,
      viaturaHabitual: o.viaturaHabitualDeviceId != null || o.viaturaHabitualMatricula
        ? { deviceId: o.viaturaHabitualDeviceId ?? null, matricula: o.viaturaHabitualMatricula || '' } : null,
      favorito: !!o.favoriteLocationId, hadFavorite: !!o.favoriteLocationId,
      favMode: 'create', favLinkId: null,
    });
    setEditingId(o.id);
  }
  function cancelEdit() { setEditingId(null); setForm(EMPTY_FORM); }

  function setContactoExtra(i, field, value) {
    const arr = [...(form.contactosExtra || [])];
    arr[i] = { ...(arr[i] || {}), [field]: value };
    setForm({ ...form, contactosExtra: arr });
  }
  function addContactoExtra() { setForm({ ...form, contactosExtra: [...(form.contactosExtra || []), { nome: '', telefone: '', email: '' }] }); }
  function removeContactoExtra(i) { setForm({ ...form, contactosExtra: (form.contactosExtra || []).filter((_, k) => k !== i) }); }

  async function saveForm() {
    if (!form.codigoObra.trim()) {
      setError(pt ? 'Código de obra é obrigatório.' : 'Work code is required.');
      return;
    }
    setSaving(true); setError(null);
    try {
      const { lat, lng } = parseCoords(form.coordsText);
      // Articulação com favorito:
      //  · desligou um que existia → removeFavorite
      //  · ligado + modo "associar" + favorito escolhido → locationId
      //  · ligado (novo ou já existente) → createFavorite (cria ou sincroniza)
      const favFlags = {};
      if (!form.favorito && form.hadFavorite) {
        favFlags.removeFavorite = true;
      } else if (form.favorito) {
        if (!form.hadFavorite && form.favMode === 'link' && form.favLinkId) {
          favFlags.locationId = form.favLinkId;
        } else {
          favFlags.createFavorite = true;
        }
      }
      const body = JSON.stringify({
        codigoObra: form.codigoObra.trim(),
        pepCc: form.pepCc.trim() || null,
        name: form.name.trim() || null,
        empresa: form.empresa.trim() || null,
        morada: form.morada.trim() || null,
        lat, lng,
        tipo: form.tipo || 'obra',
        isActive: !!form.isActive,
        dataInicio: form.dataInicio || null,
        dataFecho: form.dataFecho || null,
        responsavelNome: form.responsavelNome.trim() || null,
        responsavelTelefone: form.responsavelTelefone.trim() || null,
        responsavelEmail: form.responsavelEmail.trim() || null,
        contactosExtra: (form.contactosExtra || []).filter(c => c && (c.nome || c.telefone || c.email)),
        notes: form.notes.trim() || null,
        motoristaHabitualExternalId: form.motoristaHabitual?.externalId || null,
        motoristaHabitualNome: form.motoristaHabitual?.name || null,
        viaturaHabitualDeviceId: form.viaturaHabitual?.deviceId ?? null,
        viaturaHabitualMatricula: form.viaturaHabitual?.matricula || null,
        ...favFlags,
      });
      const isNew = editingId === 'new';
      const url = isNew ? '/planner-api/obras' : `/planner-api/obras/${editingId}`;
      const method = isNew ? 'POST' : 'PUT';
      const r = await fetch(url, { method, headers, body });
      if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.error || `HTTP ${r.status}`); }
      setEditingId(null); setForm(EMPTY_FORM); await fetchAll();
      // A gravação pode ter criado/ligado/removido um FAVORITO (ou sincronizado
      // as coordenadas de um já ligado). Refresca o store de favoritos para que
      // a página Favoritos e o mapa reflitam logo — sem ter de re-entrar na app.
      if (Object.keys(favFlags).length > 0) {
        try { dispatch(fetchLocations(token)); } catch { /* ignore */ }
      }
    } catch (e) {
      setError(e.message || (pt ? 'Erro ao guardar.' : 'Save error.'));
    } finally { setSaving(false); }
  }

  async function toggleActive(o) {
    try {
      const r = await fetch(`/planner-api/obras/${o.id}`, { method: 'PUT', headers, body: JSON.stringify({ isActive: !o.isActive }) });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await fetchAll();
    } catch (e) { setError(e.message); }
  }
  async function confirmDeleteNow() {
    if (!deleteConfirm) return;
    try {
      // Obra inativa → apaga DEFINITIVAMENTE (?hard=1); ativa → soft-delete.
      const url = `/planner-api/obras/${deleteConfirm.id}${deleteConfirm.hard ? '?hard=1' : ''}`;
      const r = await fetch(url, { method: 'DELETE', headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setDeleteConfirm(null); await fetchAll();
    } catch (e) { setError(e.message); setDeleteConfirm(null); }
  }

  const filtered = obras.filter(o => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return [o.codigoObra, o.pepCc, o.name, o.empresa].some(v => v && String(v).toLowerCase().includes(q));
  });

  return (
    <ThemeProvider theme={dlgTheme}>
    <Box className={classes.root} data-planner-theme={theme}>
      <Box className={classes.header}>
        <Tooltip title={pt ? 'Voltar a Manutenção' : 'Back to Maintenance'}>
          <IconButton size="small" onClick={() => dispatch(setActiveView('maintenance'))}><ArrowBackIcon style={{ fontSize: 20 }} /></IconButton>
        </Tooltip>
        <Box className={classes.titleBox}>
          <Typography className={classes.title}>
            <Box component="span" className={classes.headerIcon}
              style={newUI ? { background: brandGrad, boxShadow: '0 6px 16px rgba(24,189,238,0.34)' } : undefined}>
              <EngineeringIcon style={{ fontSize: 19, color: newUI ? ON_BRAND : '#0369a1' }} /></Box>
            {pt ? 'Obras' : 'Works'}
          </Typography>
          <Typography className={classes.subtitle}>
            {pt ? 'Estaleiros / obras — destino das entregas (migração CasaisHub)' : 'Construction sites — delivery destinations'}
          </Typography>
        </Box>
        <Tooltip title={pt ? 'Recarregar' : 'Refresh'}>
          <IconButton size="small" onClick={fetchAll} disabled={loading}><RefreshIcon style={{ fontSize: 18 }} /></IconButton>
        </Tooltip>
      </Box>

      <Box className={classes.toolbar}>
        <TextField className={classes.searchInput} size="small" variant="outlined"
          placeholder={pt ? 'Procurar por código, PEP, nome, empresa…' : 'Search…'}
          value={search} onChange={e => setSearch(e.target.value)} />
        <FormControlLabel
          control={<Switch size="small" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />}
          label={<Typography style={{ fontSize: '0.75rem', color: '#475569' }}>{pt ? 'Mostrar inativas' : 'Show inactive'}</Typography>} />
        <Box style={{ flex: 1 }} />
        <Button className={classes.addButton} startIcon={<AddIcon style={{ fontSize: 18 }} />} onClick={startCreate} disabled={editingId !== null}
          style={newUI ? { background: brandGrad, color: ON_BRAND, boxShadow: '0 6px 16px rgba(24,189,238,0.3)' } : undefined}>
          {pt ? 'Nova obra' : 'New work'}
        </Button>
      </Box>

      <Box className={classes.list}>
        {error && (
          <Box style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 8, marginBottom: 12, fontSize: '0.78rem' }}>{error}</Box>
        )}

        {editingId !== null && (
          <Box ref={formCardRef} className={classes.formCard}>
            <Typography className={classes.formTitle}>
              <EngineeringIcon style={{ fontSize: 18, color: '#0369a1' }} />
              {editingId === 'new' ? (pt ? 'Nova obra' : 'New work') : (pt ? 'Editar obra' : 'Edit work')}
            </Typography>
            <Box className={classes.formRow}>
              <TextField label={pt ? 'Código de Obra *' : 'Work code *'} size="small" variant="outlined" autoFocus
                value={form.codigoObra} onChange={e => setForm({ ...form, codigoObra: e.target.value })} />
              <TextField label="PEP / CC (SAP)" size="small" variant="outlined"
                value={form.pepCc} onChange={e => setForm({ ...form, pepCc: e.target.value })} />
            </Box>
            <TextField label={pt ? 'Nome / Descrição' : 'Name / Description'} size="small" variant="outlined" fullWidth
              value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            {/* Morada com geocodificação: escrever → sugestões → preenche
                morada + coordenadas (igual aos favoritos). */}
            <Box style={{ position: 'relative' }}>
              <TextField label={pt ? 'Morada' : 'Address'} size="small" variant="outlined" fullWidth
                placeholder={pt ? 'Ex: Rua da Indústria 45, Braga' : 'Ex: 45 Industry St'}
                value={form.morada}
                onFocus={() => setAddrFocused(true)}
                onBlur={() => setTimeout(() => setAddrFocused(false), 200)}
                onChange={e => {
                  const val = e.target.value;
                  setForm(f => ({ ...f, morada: val }));
                  if (addrDebounceRef.current) clearTimeout(addrDebounceRef.current);
                  addrDebounceRef.current = setTimeout(async () => {
                    if (val.trim().length >= 3) {
                      try {
                        const results = await geocodeSearchMulti(val, { country: clientSettings?.defaultCountry || 'PT' });
                        setAddrSuggestions(results || []);
                      } catch { setAddrSuggestions([]); }
                    } else { setAddrSuggestions([]); }
                  }, 350);
                }} />
              {addrFocused && addrSuggestions.length > 0 && (
                <Box style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 8px 24px rgba(15,23,42,0.18)', maxHeight: 240, overflowY: 'auto', marginTop: 2 }}>
                  {addrSuggestions.map((s, i) => (
                    <Box key={i}
                      onMouseDown={() => {
                        setForm(f => ({
                          ...f,
                          morada: s.name,
                          coordsText: (Array.isArray(s.coordinates) && s.coordinates.length === 2)
                            ? `${s.coordinates[1]}, ${s.coordinates[0]}` : f.coordsText,
                        }));
                        setAddrSuggestions([]);
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '0.74rem' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                      <PlaceIcon style={{ fontSize: 14, color: '#94a3b8', flexShrink: 0 }} />
                      <Typography style={{ fontSize: '0.74rem', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            <Box className={classes.formRow}>
              <TextField label={pt ? 'Empresa' : 'Company'} size="small" variant="outlined"
                value={form.empresa} onChange={e => setForm({ ...form, empresa: e.target.value })} />
              <TextField label={pt ? 'Coordenadas (lat, lng)' : 'Coordinates (lat, lng)'} size="small" variant="outlined"
                placeholder="41.566398, -8.484396"
                value={form.coordsText} onChange={e => setForm({ ...form, coordsText: e.target.value })} />
            </Box>
            <Box style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 12px' }}>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <FormControlLabel style={{ margin: 0 }}
                  control={<Switch size="small" color="primary" checked={!!form.favorito}
                    onChange={e => setForm({ ...form, favorito: e.target.checked })} />}
                  label={<Typography style={{ fontSize: '0.8rem', color: '#92400e', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><PlaceIcon style={{ fontSize: 16, color: '#f59e0b' }} /> {pt ? 'Mostrar no mapa (favorito)' : 'Show on map (favorite)'}</Typography>} />
                {form.hadFavorite && form.favorito && (
                  <Chip label={pt ? 'Ligado' : 'Linked'} size="small" style={{ height: 20, fontSize: '0.62rem', fontWeight: 700, background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }} />
                )}
              </Box>
              {/* Ao ligar de novo (ainda sem favorito): criar novo OU associar existente. */}
              {form.favorito && !form.hadFavorite && (
                <Box style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Box style={{ display: 'flex', gap: 8 }}>
                    {['create', 'link'].map(m => {
                      const active = form.favMode === m;
                      return (
                        <Box key={m} onClick={() => setForm({ ...form, favMode: m })}
                          style={{ cursor: 'pointer', flex: 1, textAlign: 'center', padding: '6px 10px', borderRadius: 8, fontSize: '0.72rem', fontWeight: 700,
                            background: active ? '#f59e0b' : '#fff', color: active ? '#fff' : '#92400e', border: `1px solid ${active ? '#f59e0b' : '#fcd34d'}` }}>
                          {m === 'create' ? (pt ? 'Criar novo favorito' : 'Create new') : (pt ? 'Associar existente' : 'Link existing')}
                        </Box>
                      );
                    })}
                  </Box>
                  {form.favMode === 'link' && (
                    <Autocomplete
                      size="small"
                      options={favList.filter(f => !f.obraId)}
                      value={favList.find(f => Number(f.id) === Number(form.favLinkId)) || null}
                      getOptionLabel={(f) => f ? `${f.name}${f.address ? ` · ${f.address}` : ''}` : ''}
                      getOptionSelected={(f, v) => f.id === v.id}
                      onChange={(e, v) => setForm({ ...form, favLinkId: v ? v.id : null })}
                      renderInput={(params) => (
                        <TextField {...params} variant="outlined" placeholder={pt ? 'Escolher favorito existente…' : 'Pick favorite…'} />
                      )}
                    />
                  )}
                </Box>
              )}
              <Typography style={{ fontSize: '0.66rem', color: '#a16207', marginTop: 6 }}>
                {pt ? 'Favorito fica como tipo "Outros" + categoria "Obra"; as coordenadas sincronizam (último a gravar manda).' : 'Favorite = type "Other" + category "Obra"; coordinates sync.'}
              </Typography>
            </Box>
            <Box className={classes.formRow} style={{ gridTemplateColumns: '1fr 1fr 1fr', alignItems: 'center' }}>
              <TextField label={pt ? 'Data início' : 'Start date'} type="date" size="small" variant="outlined" InputLabelProps={{ shrink: true }}
                value={form.dataInicio} onChange={e => setForm({ ...form, dataInicio: e.target.value })} />
              <TextField label={pt ? 'Data fecho' : 'End date'} type="date" size="small" variant="outlined" InputLabelProps={{ shrink: true }}
                value={form.dataFecho} onChange={e => setForm({ ...form, dataFecho: e.target.value })} />
              <FormControlLabel style={{ margin: 0 }}
                control={<Switch size="small" color="primary" checked={!!form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} />}
                label={<Typography style={{ fontSize: '0.78rem', color: '#475569' }}>{pt ? 'Ativa' : 'Active'}</Typography>} />
            </Box>

            <Typography className={classes.sectionLbl}><PhoneIcon style={{ fontSize: 14 }} /> {pt ? 'Responsável da obra' : 'Site contact'}</Typography>
            <Box className={classes.formRow} style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              <TextField label={pt ? 'Nome' : 'Name'} size="small" variant="outlined"
                value={form.responsavelNome} onChange={e => setForm({ ...form, responsavelNome: e.target.value })} />
              <TextField label={pt ? 'Telefone' : 'Phone'} size="small" variant="outlined"
                value={form.responsavelTelefone} onChange={e => setForm({ ...form, responsavelTelefone: e.target.value })} />
              <TextField label="Email" size="small" variant="outlined"
                value={form.responsavelEmail} onChange={e => setForm({ ...form, responsavelEmail: e.target.value })} />
            </Box>

            {(form.contactosExtra || []).map((c, i) => (
              <Box key={i} className={classes.formRow} style={{ gridTemplateColumns: '1fr 1fr 1fr 32px', alignItems: 'center' }}>
                <TextField label={pt ? `Contacto extra ${i + 1} — Nome` : `Extra contact ${i + 1} — Name`} size="small" variant="outlined"
                  value={c.nome || ''} onChange={e => setContactoExtra(i, 'nome', e.target.value)} />
                <TextField label={pt ? 'Telefone' : 'Phone'} size="small" variant="outlined"
                  value={c.telefone || ''} onChange={e => setContactoExtra(i, 'telefone', e.target.value)} />
                <TextField label="Email" size="small" variant="outlined"
                  value={c.email || ''} onChange={e => setContactoExtra(i, 'email', e.target.value)} />
                <IconButton size="small" onClick={() => removeContactoExtra(i)}><CloseIcon style={{ fontSize: 16, color: '#ef4444' }} /></IconButton>
              </Box>
            ))}
            <Button onClick={addContactoExtra} startIcon={<AddIcon style={{ fontSize: 16 }} />}
              style={{ textTransform: 'none', fontSize: '0.72rem', color: '#0369a1', alignSelf: 'flex-start', padding: '2px 8px' }}>
              {pt ? 'Contacto extra' : 'Extra contact'}
            </Button>

            <Typography className={classes.sectionLbl}>🚚 {pt ? 'Habituais (usados no import quando o ficheiro não os traz)' : 'Defaults (used by import)'}</Typography>
            <Box className={classes.formRow}>
              <Autocomplete
                size="small"
                options={driversList.map(d => ({ externalId: d.externalId || null, name: d.name }))}
                getOptionLabel={o => o.name || ''}
                getOptionSelected={(o, v) => (o.externalId && v.externalId ? o.externalId === v.externalId : o.name === v.name)}
                value={form.motoristaHabitual}
                onChange={(e, v) => setForm({ ...form, motoristaHabitual: v ? { externalId: v.externalId || null, name: v.name } : null })}
                renderInput={(params) => <TextField {...params} label={pt ? 'Motorista habitual' : 'Default driver'} variant="outlined" size="small" />}
              />
              <Autocomplete
                size="small"
                options={vehiclesList.map(v => ({ deviceId: v.id, matricula: v.plate || v.name || `#${v.id}` }))}
                getOptionLabel={o => o.matricula || ''}
                getOptionSelected={(o, v) => o.deviceId === v.deviceId}
                value={form.viaturaHabitual}
                onChange={(e, v) => setForm({ ...form, viaturaHabitual: v ? { deviceId: v.deviceId, matricula: v.matricula } : null })}
                renderInput={(params) => <TextField {...params} label={pt ? 'Viatura habitual' : 'Default vehicle'} variant="outlined" size="small" />}
              />
            </Box>

            <TextField label={pt ? 'Notas' : 'Notes'} size="small" variant="outlined" fullWidth multiline minRows={2}
              value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />

            <Box className={classes.formActions}>
              <Button onClick={cancelEdit} style={{ textTransform: 'none', fontSize: '0.78rem' }} disabled={saving}>{pt ? 'Cancelar' : 'Cancel'}</Button>
              <Button onClick={saveForm} disabled={saving} className={classes.saveButton}
                style={newUI ? { background: brandGrad, color: ON_BRAND, boxShadow: '0 6px 16px rgba(24,189,238,0.3)' } : undefined}
                startIcon={saving ? <CircularProgress size={14} style={{ color: newUI ? ON_BRAND : '#fff' }} /> : <CheckIcon style={{ fontSize: 16 }} />}>
                {pt ? 'Guardar Alterações' : 'Save'}
              </Button>
            </Box>
          </Box>
        )}

        {loading && obras.length === 0 ? (
          <Box className={classes.empty}><CircularProgress size={32} style={{ color: '#0369a1' }} /><Typography style={{ fontSize: '0.8rem' }}>{pt ? 'A carregar…' : 'Loading…'}</Typography></Box>
        ) : filtered.length === 0 ? (
          <Box className={classes.empty}>
            {newUI ? (
              <Box style={{ width: 66, height: 66, borderRadius: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surf-head-bg, #f1f6f9)', color: theme === 'dark' ? '#6f93a6' : '#94a3b8' }}>
                <EngineeringIcon style={{ fontSize: 32 }} />
              </Box>
            ) : (
              <EngineeringIcon style={{ fontSize: 48, color: '#cbd5e1' }} />
            )}
            <Typography style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--surf-title, #0f172a)' }}>
              {search ? (pt ? 'Sem resultados.' : 'No results.') : (pt ? 'Ainda não há obras.' : 'No works yet.')}
            </Typography>
            {!search && <Typography style={{ fontSize: '0.8rem', color: 'var(--surf-muted, #64748b)', maxWidth: 380, textAlign: 'center', marginTop: -4 }}>
              {pt ? 'Regista a primeira obra (estaleiro de destino das entregas).' : 'Register the first work (delivery destination).'}
            </Typography>}
            {!search && <Button className={classes.addButton} startIcon={<AddIcon style={{ fontSize: 18 }} />} onClick={startCreate} disabled={editingId !== null}
              style={newUI ? { background: brandGrad, color: ON_BRAND, boxShadow: '0 6px 16px rgba(24,189,238,0.3)' } : undefined}>{pt ? 'Criar primeira' : 'Create first'}</Button>}
          </Box>
        ) : (
          filtered.map(o => (
            <Box key={o.id} className={`${classes.card} ${!o.isActive ? classes.cardInactive : ''}`}>
              <Box className={classes.cardIcon}><EngineeringIcon style={{ fontSize: 20, color: '#0369a1' }} /></Box>
              <Box className={classes.cardMain}>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Typography className={classes.cardName}>{o.codigoObra}{o.name ? ` · ${o.name}` : ''}</Typography>
                  {!o.isActive && <Chip label={pt ? 'Inativa' : 'Inactive'} size="small" style={{ height: 18, fontSize: '0.6rem', background: '#e2e8f0', color: '#475569' }} />}
                  {o.favoriteLocationId && <Chip icon={<PlaceIcon style={{ fontSize: 12 }} />} label={pt ? 'No mapa' : 'On map'} size="small" style={{ height: 18, fontSize: '0.6rem', background: '#fef3c7', color: '#92400e' }} />}
                </Box>
                <Box className={classes.cardMeta}>
                  {o.pepCc && <span>PEP/CC: {o.pepCc}</span>}
                  {o.empresa && <span>{o.empresa}</span>}
                  {o.morada && <span style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📍 {o.morada}</span>}
                  {(o.lat != null && o.lng != null) && <span><PlaceIcon style={{ fontSize: 12, verticalAlign: 'middle' }} /> {Number(o.lat).toFixed(4)}, {Number(o.lng).toFixed(4)}</span>}
                  {o.responsavelNome && <span>👷 {o.responsavelNome}{o.responsavelTelefone ? ` · ${o.responsavelTelefone}` : ''}</span>}
                </Box>
              </Box>
              <Box className={classes.cardActions}>
                <Tooltip title={o.isActive ? (pt ? 'Desativar' : 'Deactivate') : (pt ? 'Reativar' : 'Reactivate')}>
                  <IconButton size="small" onClick={() => toggleActive(o)}>
                    {o.isActive ? <CloseIcon style={{ fontSize: 18, color: '#94a3b8' }} /> : <CheckIcon style={{ fontSize: 18, color: '#22c55e' }} />}
                  </IconButton>
                </Tooltip>
                <Tooltip title={pt ? 'Editar' : 'Edit'}>
                  <IconButton size="small" onClick={() => startEdit(o)} disabled={editingId !== null}><EditIcon style={{ fontSize: 16, color: '#0369a1' }} /></IconButton>
                </Tooltip>
                <Tooltip title={o.isActive ? (pt ? 'Desativar (reversível)' : 'Deactivate') : (pt ? 'Apagar definitivamente' : 'Delete permanently')}>
                  <IconButton size="small" onClick={() => setDeleteConfirm({ id: o.id, name: o.codigoObra, hard: !o.isActive })}><DeleteIcon style={{ fontSize: 16, color: '#ef4444' }} /></IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))
        )}
      </Box>

      <ConfirmDialog
        open={!!deleteConfirm}
        title={deleteConfirm?.hard ? (pt ? 'Apagar obra definitivamente?' : 'Delete work permanently?') : (pt ? 'Desativar obra?' : 'Deactivate work?')}
        message={deleteConfirm ? (deleteConfirm.hard
          ? (pt ? `A obra "${deleteConfirm.name}" é apagada em definitivo. Esta ação é irreversível. As OTs e favoritos ligados ficam sem obra.` : `"${deleteConfirm.name}" will be permanently deleted. This cannot be undone.`)
          : (pt ? `A obra "${deleteConfirm.name}" passa a inativa (não é apagada; pode ser reativada).` : `"${deleteConfirm.name}" will be deactivated.`)) : ''}
        severity={deleteConfirm?.hard ? 'error' : 'warning'}
        confirmLabel={deleteConfirm?.hard ? (pt ? 'Apagar definitivamente' : 'Delete permanently') : (pt ? 'Desativar' : 'Deactivate')}
        cancelLabel={pt ? 'Cancelar' : 'Cancel'}
        onConfirm={confirmDeleteNow}
        onClose={() => setDeleteConfirm(null)}
      />
    </Box>
    </ThemeProvider>
  );
}

export default ObrasView;
